import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from './supabase';
import {
  AlertCircle,
  Save,
  Settings as SettingsIcon,
  Star,
  Send,
  RefreshCw,
  Download,
  CheckCircle2,
  XCircle,
  Clock,
  Camera,
  Ticket,
} from 'lucide-react';
import './Reviews.css';

interface ReviewSettings {
  id: number;
  enabled: boolean;
  coupon_enabled: boolean;
  delay_days: number;
  rating_threshold_for_photo: number;
  discount_percent: number;
  template_initial_name: string;
  template_initial_body: string;
  template_initial_status: string;
  template_high_rating_comment_body: string;
  template_photo_thanks_name: string;
  template_photo_thanks_body: string;
  template_photo_thanks_status: string;
  template_low_rating_name: string;
  template_low_rating_body: string;
  template_low_rating_status: string;
  template_coupon_delivery_name: string;
  template_coupon_delivery_body: string;
  template_coupon_delivery_status: string;
  updated_at: string;
}

interface ReviewRow {
  id: string;
  order_id: number;
  phone: string;
  contact_name: string | null;
  sent_at: string;
  responded_at: string | null;
  rating: number | null;
  photo_url: string | null;
  media?: { url: string; mime?: string; at?: string }[] | null;
  photo_received_at: string | null;
  discount_code: string | null;
  comment: string | null;
  comment_received_at: string | null;
  status: string;
  last_error: string | null;
  created_at: string;
  // join con orders para mostrar display_id y email en el export
  orders?: {
    id: number;
    tiendanube_id: string | null;
    source: string | null;
    contact_email: string | null;
    products: any;
  } | null;
}

interface Props {
  userEmail: string | undefined;
}

const ALLOWED_EMAILS = ['martin@krilic.com.ar', 'stephanie@krilic.com.ar'];

// Resumen de productos de la orden para la lista de reviews: nombres separados por coma,
// abreviados (sin la variante " - Color") y con cantidad si es > 1. Ej: "2× Estante Pacman, Lámpara Silueta"
function productsSummary(order: ReviewRow['orders']): string {
  const prods = Array.isArray(order?.products) ? order!.products : [];
  const names = prods.map((p: any) => {
    const base = String(p?.name || p?.title || '').split(/\s[-–]\s/)[0].trim();
    const qty = Number(p?.quantity) || 1;
    return base ? (qty > 1 ? `${qty}× ${base}` : base) : '';
  }).filter(Boolean);
  return names.join(', ');
}

const TEMPLATE_KEYS = [
  {
    key: 'initial',
    label: 'Pedido de review inicial',
    helper: 'Variables: {{1}}=nombre, {{2}}=listado de productos. Sin botones: el cliente responde el rating como texto ("1"-"5"). Se envía después del delay configurado.',
    buttons: [] as string[],
    isMetaTemplate: true,
  },
  {
    key: 'high_rating_comment',
    label: 'Pedido de review escrita (rating alto, paso 1)',
    helper: 'Se manda después de un rating ≥ threshold, antes de pedir la foto. Variables: {{1}}=nombre. Texto libre, no requiere aprobación de Meta.',
    buttons: [],
    isMetaTemplate: false,
  },
  {
    key: 'photo_thanks',
    label: 'Pedido de foto (rating alto, paso 2)',
    helper: 'Se manda después de que el cliente escribe la review. Variables: {{1}}=nombre, {{2}}=descuento %. Texto libre, no requiere aprobación de Meta.',
    buttons: [],
    isMetaTemplate: false,
  },
  {
    key: 'low_rating',
    label: 'Respuesta a rating bajo',
    helper: 'Variables: {{1}}=nombre, {{2}}=descuento %. Texto libre en ventana de 24hs, no requiere aprobación.',
    buttons: [],
    isMetaTemplate: false,
  },
  {
    key: 'coupon_delivery',
    label: 'Entrega del cupón',
    helper: 'Variables: {{1}}=nombre, {{2}}=descuento %, {{3}}=código de cupón. Texto libre, no requiere aprobación.',
    buttons: [],
    isMetaTemplate: false,
  },
] as const;

const STATUS_BADGE: Record<string, { label: string; color: string }> = {
  NONE: { label: 'No creado', color: '#94a3b8' },
  PENDING: { label: 'En aprobación', color: '#f59e0b' },
  APPROVED: { label: 'Aprobado', color: '#16a34a' },
  REJECTED: { label: 'Rechazado', color: '#dc2626' },
};

function Reviews({ userEmail }: Props) {
  const allowed = !!userEmail && ALLOWED_EMAILS.includes(userEmail);
  const [subTab, setSubTab] = useState<'config' | 'list'>('config');
  const [listFilter, setListFilter] = useState<'all' | 'reclamo' | 'rated' | 'sent'>('all');
  const [settings, setSettings] = useState<ReviewSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submittingTpl, setSubmittingTpl] = useState<string | null>(null);
  const [reviews, setReviews] = useState<ReviewRow[]>([]);
  // Entrega del pedido de review por WhatsApp (18/10/2026): ✓ enviado / ✓✓ entregado / ✓✓ leído / ✗.
  // Sale de whatsapp_logs (estados que manda Meta por el webhook, migr 109) — por teléfono + fecha.
  const [waDelivery, setWaDelivery] = useState<Map<string, { status: string; delivered_at: string | null; read_at: string | null; error: string | null }>>(new Map());
  const [reviewsLoading, setReviewsLoading] = useState(false);
  // Paginación server-side: 100 por página, "Cargar más" apila. Con 1500+ reviews
  // bajar todo de una (select * + join de orders) pesaba varios cientos de kB por visita.
  const PAGE_SIZE = 100;
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterCounts, setFilterCounts] = useState<{ all: number; reclamo: number; rated: number; sent: number }>({ all: 0, reclamo: 0, rated: 0, sent: 0 });
  const [globalStats, setGlobalStats] = useState<{
    total: number;
    rated: number;
    withPhoto: number;
    withCoupon: number;
    ratingDist: { rating: number; count: number }[];
    avg: number;
  } | null>(null);

  const [pipeline, setPipeline] = useState<{
    en_cola: number;
    entregados_menos_10d: number;
    sin_entregar: number;
    enviados_total: number;
  } | null>(null);

  useEffect(() => {
    if (allowed) {
      loadSettings();
      loadPipeline();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  async function loadPipeline() {
    const { data, error } = await supabase.rpc('review_pipeline_stats');
    if (!error && data) setPipeline(data as any);
  }

  useEffect(() => {
    if (allowed && subTab === 'list') loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, subTab]);

  async function loadSettings() {
    setLoading(true);
    const { data, error } = await supabase.from('review_settings').select('*').eq('id', 1).single();
    if (error) {
      console.error(error);
      alert('Error cargando settings: ' + error.message);
    } else {
      setSettings(data as ReviewSettings);
    }
    setLoading(false);
  }

  function applyFilter(q: any, f: typeof listFilter) {
    if (f === 'reclamo') return q.eq('status', 'reclamo');
    if (f === 'rated') return q.not('rating', 'is', null);
    if (f === 'sent') return q.eq('status', 'sent');
    return q;
  }

  async function loadWaDelivery(rows: ReviewRow[]) {
    const sent = rows.filter(r => r.sent_at && r.phone);
    if (!sent.length) return;
    const times = sent.map(r => new Date(r.sent_at as any).getTime());
    const from = new Date(Math.min(...times) - 86400000).toISOString(), to = new Date(Math.max(...times) + 86400000).toISOString();
    const { data } = await supabase.from('whatsapp_logs')
      .select('phone, created_at, status, delivered_at, read_at, error_message')
      .eq('template_name', 'review_request_initial_v4').gte('created_at', from).lte('created_at', to).limit(2000);
    const tail = (p: string) => String(p || '').replace(/\D/g, '').slice(-8);
    const byTail = new Map<string, any[]>();
    for (const l of (data || [])) { const t = tail(l.phone); if (!byTail.has(t)) byTail.set(t, []); byTail.get(t)!.push(l); }
    setWaDelivery(prev => {
      const next = new Map(prev);
      for (const r of sent) {
        const ts = new Date(r.sent_at as any).getTime();
        const l = (byTail.get(tail(r.phone as any)) || []).sort((a, b) => Math.abs(new Date(a.created_at).getTime() - ts) - Math.abs(new Date(b.created_at).getTime() - ts))[0];
        if (l && Math.abs(new Date(l.created_at).getTime() - ts) < 86400000) next.set(String(r.id), { status: l.status, delivered_at: l.delivered_at, read_at: l.read_at, error: l.error_message });
      }
      return next;
    });
  }

  async function loadReviews(opts?: { page?: number; filter?: typeof listFilter }) {
    const page = opts?.page ?? 0;
    const filter = opts?.filter ?? listFilter;
    if (page === 0) setReviewsLoading(true); else setLoadingMore(true);
    const pageQ = applyFilter(
      supabase
        .from('reviews')
        .select('*, orders(id, tiendanube_id, source, contact_email, products)')
        .order('sent_at', { ascending: false, nullsFirst: false }),
      filter,
    ).range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);
    const { data: pageRows, error: pageErr } = await pageQ;
    if (pageErr) { alert('Error cargando reviews: ' + pageErr.message); setReviewsLoading(false); setLoadingMore(false); return; }
    setReviews(prev => page === 0 ? ((pageRows || []) as ReviewRow[]) : [...prev, ...((pageRows || []) as ReviewRow[])]);
    loadWaDelivery((pageRows || []) as ReviewRow[]);
    setHasMore((pageRows || []).length === PAGE_SIZE);
    setReviewsLoading(false); setLoadingMore(false);
    if (page > 0) return; // los stats globales solo hacen falta una vez

    const countQ = () => supabase.from('reviews').select('id', { count: 'exact', head: true });
    const [totalRes, ratedRes, photoRes, couponRes, reclamoRes, sentRes, r1, r2, r3, r4, r5] = await Promise.all([
      countQ(),
      countQ().not('rating', 'is', null),
      countQ().not('photo_url', 'is', null),
      countQ().not('discount_code', 'is', null),
      countQ().eq('status', 'reclamo'),
      countQ().eq('status', 'sent'),
      countQ().eq('rating', 1),
      countQ().eq('rating', 2),
      countQ().eq('rating', 3),
      countQ().eq('rating', 4),
      countQ().eq('rating', 5),
    ]);
    setFilterCounts({ all: totalRes.count ?? 0, reclamo: reclamoRes.count ?? 0, rated: ratedRes.count ?? 0, sent: sentRes.count ?? 0 });
    legacyStats(totalRes, ratedRes, photoRes, couponRes, [r1, r2, r3, r4, r5]);
  }

  function legacyStats(totalRes: any, ratedRes: any, photoRes: any, couponRes: any, rs: any[]) {
    const [r1, r2, r3, r4, r5] = rs;
    const ratingDist = [1, 2, 3, 4, 5].map((n, i) => ({
      rating: n,
      count: [r1, r2, r3, r4, r5][i].count ?? 0,
    }));
    const rated = ratedRes.count ?? 0;
    const ratingSum = ratingDist.reduce((s, d) => s + d.rating * d.count, 0);
    setGlobalStats({
      total: totalRes.count ?? 0,
      rated,
      withPhoto: photoRes.count ?? 0,
      withCoupon: couponRes.count ?? 0,
      ratingDist,
      avg: rated > 0 ? ratingSum / rated : 0,
    });
  }

  function getOrderDisplayId(o: { id: number; tiendanube_id: string | null; source: string | null } | null | undefined): string {
    if (!o) return '';
    const src = (o.source || '').toLowerCase();
    if (src === 'woocommerce' && o.tiendanube_id) return `BT-${o.tiendanube_id}`;
    if ((src === 'mercadolibre' || src === 'meli') && o.tiendanube_id) return `ML-${o.tiendanube_id}`;
    if (o.tiendanube_id) return String(o.tiendanube_id);
    return `K-${o.id}`;
  }

  function csvEscape(value: any): string {
    if (value === null || value === undefined) return '';
    const s = String(value);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  }

  async function exportReviewsCsv() {
    // Con la lista paginada, el export consulta TODO al server (no solo la página visible).
    const { data, error } = await supabase
      .from('reviews')
      .select('contact_name, rating, comment, responded_at, sent_at, phone, photo_url, orders(id, tiendanube_id, source, contact_email)')
      .not('rating', 'is', null)
      .order('sent_at', { ascending: false })
      .limit(10000);
    if (error) { alert('Error al leer las reviews: ' + error.message); return; }
    const rows = (data || []) as unknown as ReviewRow[];
    if (rows.length === 0) {
      alert('No hay reviews con rating para exportar.');
      return;
    }
    const headers = ['name', 'rating', 'comment', 'date', 'phone', 'email', 'order_id', 'photo'];
    const lines: string[] = [headers.join(',')];
    for (const r of rows) {
      const dateIso = r.responded_at || r.sent_at;
      const date = dateIso ? new Date(dateIso).toISOString().slice(0, 10) : '';
      const cells = [
        r.contact_name || '',
        r.rating ?? '',
        r.comment || '',
        date,
        r.phone || '',
        r.orders?.contact_email || '',
        getOrderDisplayId(r.orders),
        r.photo_url || '',
      ];
      lines.push(cells.map(csvEscape).join(','));
    }
    const csv = '﻿' + lines.join('\n'); // BOM para Excel/Sheets en UTF-8
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `reviews-krilic-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Export para IMPORTAR EN REVIE: solo reviews con contenido real (estrellas o comentario)
  // y con producto de Tiendanube asociado. Usa la vista reviews_by_product, así una review
  // que aplica a varios productos (ej. Parlante Cassete Negro/Blanco) sale una línea POR
  // producto. Columnas = las del CSV de Revie.
  async function exportRevieCsv() {
    const { data, error } = await supabase
      .from('reviews_by_product')
      .select('id, applies_to_product_id, order_id, rating, comment, contact_name, phone, photo_url, media, responded_at, comment_received_at, sent_at, created_at, meta')
      .limit(10000);
    if (error) { alert('Error al leer las reviews: ' + error.message); return; }
    const rows = (data || []).filter((r: any) =>
      (r.rating != null || (r.comment && r.comment.trim() !== ''))
      // Las importadas DE Revie ya viven allá: re-exportarlas duplicaría todo en Revie.
      && (r.meta?.source !== 'revie'));
    if (rows.length === 0) { alert('No hay reviews con contenido y producto asociado.'); return; }

    // Nombres de producto y display id de orden en dos queries chicas
    const prodIds = Array.from(new Set(rows.map((r: any) => r.applies_to_product_id)));
    const orderIds = Array.from(new Set(rows.map((r: any) => r.order_id)));
    const [prodRes, ordRes] = await Promise.all([
      supabase.from('tn_products').select('id, name').in('id', prodIds),
      supabase.from('orders').select('id, tiendanube_id, source').in('id', orderIds),
    ]);
    const prodName = new Map((prodRes.data || []).map((p: any) => [p.id, p.name]));
    const ordById = new Map((ordRes.data || []).map((o: any) => [o.id, o]));

    const headers = ['Id', 'CreatedAt', 'Stars', 'Comment', 'State', 'CustomerName', 'CustomerPhone', 'OrderName', 'ProductId', 'ProductName', 'ImageUrl'];
    const lines: string[] = [headers.join(',')];
    const seen = new Map<string, number>(); // review id -> cuántas líneas lleva (sufijo -2, -3…)
    for (const r of rows as any[]) {
      const n = (seen.get(r.id) || 0) + 1;
      seen.set(r.id, n);
      const firstImg = r.photo_url || (Array.isArray(r.media) ? (r.media.find((m: any) => String(m?.mime || '').startsWith('image/'))?.url || '') : '');
      const created = r.comment_received_at || r.responded_at || r.sent_at || r.created_at;
      lines.push([
        n === 1 ? r.id : `${r.id}-${n}`,
        created ? new Date(created).toISOString() : '',
        r.rating ?? '',
        r.comment || '',
        'APPROVED',
        r.contact_name || '',
        r.phone || '',
        getOrderDisplayId(ordById.get(r.order_id)),
        r.applies_to_product_id,
        prodName.get(r.applies_to_product_id) || '',
        firstImg,
      ].map(csvEscape).join(','));
    }
    const csv = '﻿' + lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reviews-revie-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function saveSettings() {
    if (!settings) return;
    setSaving(true);
    const { id, updated_at, ...payload } = settings;
    const { error } = await supabase.from('review_settings').update(payload).eq('id', 1);
    setSaving(false);
    if (error) {
      alert('Error guardando: ' + error.message);
      return;
    }
    alert('Configuración guardada.');
    loadSettings();
  }

  async function submitTemplateToMeta(key: typeof TEMPLATE_KEYS[number]['key']) {
    if (!settings) return;
    setSubmittingTpl(key);
    const tplName = (settings as any)[`template_${key}_name`] as string;
    const tplBody = (settings as any)[`template_${key}_body`] as string;
    const buttons = [...(TEMPLATE_KEYS.find(t => t.key === key)?.buttons || [])];
    try {
      const { data, error } = await supabase.functions.invoke('submit-meta-template', {
        body: { name: tplName, body: tplBody, buttons, language: 'es_AR', category: 'MARKETING' },
      });
      if (error) throw error;
      if (data?.success === false) {
        const msg = [
          data?.error_user_msg || data?.error || 'Error',
          data?.error_subcode ? `subcode ${data.error_subcode}` : null,
        ].filter(Boolean).join(' · ');
        throw new Error(msg);
      }
      alert(`Template "${tplName}" enviado a Meta. Estado: ${data?.status || 'PENDING'}.`);
      const patch: any = {};
      patch[`template_${key}_status`] = data?.status || 'PENDING';
      await supabase.from('review_settings').update(patch).eq('id', 1);
      loadSettings();
    } catch (e: any) {
      alert('Error de Meta:\n\n' + (e?.message || String(e)));
    } finally {
      setSubmittingTpl(null);
    }
  }

  const stats = useMemo(() => {
    if (globalStats) return globalStats;
    return {
      total: 0,
      rated: 0,
      withPhoto: 0,
      withCoupon: 0,
      ratingDist: [1, 2, 3, 4, 5].map(n => ({ rating: n, count: 0 })),
      avg: 0,
    };
  }, [globalStats]);

  if (!allowed) {
    return (
      <div className="rv-denied">
        <AlertCircle size={48} />
        <h2>Acceso Denegado</h2>
        <p>Esta sección es exclusiva para la administración.</p>
      </div>
    );
  }

  return (
    <div className="rv-container">
      <div className="rv-header">
        <div className="rv-title">
          <Star size={22} color="#f59e0b" fill="#f59e0b" />
          <h1>Reviews</h1>
          <span className={`rv-toggle-pill ${settings?.enabled ? 'on' : 'off'}`}>
            {settings?.enabled ? '✓ Activo' : 'Inactivo'}
          </span>
        </div>
        <div className="rv-subnav">
          <button
            className={`rv-subnav-btn ${subTab === 'config' ? 'active' : ''}`}
            onClick={() => setSubTab('config')}
          >
            <SettingsIcon size={16} /> Configuración
          </button>
          <button
            className={`rv-subnav-btn ${subTab === 'list' ? 'active' : ''}`}
            onClick={() => setSubTab('list')}
          >
            <Star size={16} /> Reviews recibidos
          </button>
        </div>
      </div>

      {pipeline && (
        <section className="rv-card rv-pipeline">
          <div className="rv-pipeline-head">
            <h3>Cola de envío</h3>
            <button className="rv-pipeline-refresh" onClick={loadPipeline} title="Actualizar">
              <RefreshCw size={14} />
            </button>
          </div>
          <div className="rv-stats-row">
            <div className="rv-stat">
              <div className="rv-stat-label">En cola (por enviar)</div>
              <div className="rv-stat-value">{pipeline.en_cola}</div>
            </div>
            <div className="rv-stat">
              <div className="rv-stat-label">Faltan por enviar</div>
              <div className="rv-stat-value" style={{ color: '#f59e0b' }}>
                {pipeline.entregados_menos_10d + pipeline.sin_entregar}
              </div>
            </div>
            <div className="rv-stat">
              <div className="rv-stat-label">· Entregados hace &lt;7 días</div>
              <div className="rv-stat-value">{pipeline.entregados_menos_10d}</div>
            </div>
            <div className="rv-stat">
              <div className="rv-stat-label">· Sin entregar aún</div>
              <div className="rv-stat-value">{pipeline.sin_entregar}</div>
            </div>
            <div className="rv-stat">
              <div className="rv-stat-label">Enviados (total)</div>
              <div className="rv-stat-value">{pipeline.enviados_total}</div>
            </div>
          </div>
          <p className="rv-help" style={{ marginTop: 10 }}>
            "En cola" son los que están entregados hace 10+ días y se envían en la próxima franja horaria.
            "Faltan por enviar" son los que todavía no aplican porque no están entregados o hace menos de 10 días
            (desde mayo, con teléfono, sin contar MercadoLibre ni los que ya tienen review).
          </p>
        </section>
      )}

      {loading || !settings ? (
        <div className="rv-loading">Cargando…</div>
      ) : subTab === 'config' ? (
        <div className="rv-config">
          <section className="rv-card">
            <h3>Reglas</h3>
            <div className="rv-grid-2">
              <label className="rv-field">
                <span>Módulo activo</span>
                <div className="rv-switch-row">
                  <input
                    type="checkbox"
                    checked={settings.enabled}
                    onChange={e => setSettings({ ...settings, enabled: e.target.checked })}
                  />
                  <span className="rv-help">Si está apagado, no se manda ningún mensaje.</span>
                </div>
              </label>
              <label className="rv-field">
                <span>Días después de "entregado" para mandar review</span>
                <input
                  type="number"
                  min={1}
                  max={14}
                  value={settings.delay_days}
                  onChange={e => setSettings({ ...settings, delay_days: Math.max(1, Math.min(14, +e.target.value || 1)) })}
                />
                <span className="rv-help">Días desde la ENTREGA hasta el pedido de reseña. Rango 1-14. El cron encola cada hora y los manda en las franjas de alta conversión.</span>
              </label>
              <label className="rv-field">
                <span>Rating mínimo para pedir foto</span>
                <select
                  value={settings.rating_threshold_for_photo}
                  onChange={e => setSettings({ ...settings, rating_threshold_for_photo: +e.target.value })}
                >
                  {[1, 2, 3, 4, 5].map(n => (
                    <option key={n} value={n}>{n} estrellas</option>
                  ))}
                </select>
                <span className="rv-help">A los que califican menos solo se les manda un mensaje de agradecimiento, sin pedir foto y sin cupón.</span>
              </label>
              <label className="rv-field">
                <span>Cupón habilitado</span>
                <div className="rv-switch-row">
                  <input
                    type="checkbox"
                    checked={settings.coupon_enabled}
                    onChange={e => setSettings({ ...settings, coupon_enabled: e.target.checked })}
                  />
                  <span className="rv-help">Si está apagado, no se pide foto ni se genera cupón (solo se piden ratings).</span>
                </div>
              </label>
              <label className="rv-field">
                <span>Valor del cupón (% off)</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step="0.5"
                  value={settings.discount_percent}
                  onChange={e => setSettings({ ...settings, discount_percent: +e.target.value || 0 })}
                  disabled={!settings.coupon_enabled}
                />
                <span className="rv-help">Aplica al total, no incluye envío. Indefinido sin tope. Solo se entrega a cambio de foto.</span>
              </label>
            </div>
            <div className="rv-footer-actions">
              <button className="rv-btn rv-btn-primary" onClick={saveSettings} disabled={saving}>
                <Save size={16} /> {saving ? 'Guardando…' : 'Guardar reglas'}
              </button>
            </div>
          </section>

          <section className="rv-card">
            <h3>Templates WhatsApp</h3>
            <p className="rv-help">
              Los templates necesitan aprobación de Meta antes de poder usarse. Editá el body, guardá, y clickeá
              "Enviar a Meta". La aprobación demora minutos u horas.
            </p>
            {TEMPLATE_KEYS.map(t => {
              const nameField = `template_${t.key}_name` as keyof ReviewSettings;
              const bodyField = `template_${t.key}_body` as keyof ReviewSettings;
              const statusField = `template_${t.key}_status` as keyof ReviewSettings;
              const name = settings[nameField] as string;
              const body = settings[bodyField] as string;
              const status = (settings[statusField] as string) || 'NONE';
              const badge = STATUS_BADGE[status] || STATUS_BADGE.NONE;
              return (
                <div key={t.key} className="rv-template-card">
                  <div className="rv-template-head">
                    <div>
                      <h4>{t.label}</h4>
                      {t.isMetaTemplate ? (
                        <code className="rv-template-name">{name}</code>
                      ) : (
                        <span className="rv-template-name" style={{ background: 'transparent', borderColor: 'transparent', color: 'var(--text-secondary)' }}>
                          mensaje libre · sin aprobación de Meta
                        </span>
                      )}
                    </div>
                    {t.isMetaTemplate && (
                      <span
                        className="rv-status-chip"
                        style={{ background: `${badge.color}22`, color: badge.color, borderColor: `${badge.color}55` }}
                      >
                        {status === 'APPROVED' ? <CheckCircle2 size={12} />
                         : status === 'REJECTED' ? <XCircle size={12} />
                         : status === 'PENDING' ? <Clock size={12} />
                         : null}
                        {badge.label}
                      </span>
                    )}
                  </div>
                  {t.isMetaTemplate && (
                    <label className="rv-field">
                      <span>Nombre interno (Meta)</span>
                      <input
                        type="text"
                        value={name}
                        onChange={e => setSettings({ ...settings, [nameField]: e.target.value } as any)}
                        placeholder="solo_letras_minusculas_y_guion_bajo"
                      />
                    </label>
                  )}
                  <label className="rv-field">
                    <span>Body</span>
                    <textarea
                      rows={t.key === 'initial' ? 6 : 3}
                      value={body}
                      onChange={e => setSettings({ ...settings, [bodyField]: e.target.value } as any)}
                    />
                  </label>
                  <div className="rv-help-small">{t.helper}</div>
                  {t.buttons.length > 0 && (
                    <div className="rv-buttons-preview">
                      <span className="rv-help-small">Botones quick-reply (fijos):</span>
                      <div className="rv-buttons-row">
                        {t.buttons.map(b => (
                          <span key={b} className="rv-button-chip">{b}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {t.isMetaTemplate && (
                    <div className="rv-template-actions">
                      <button
                        className="rv-btn rv-btn-secondary"
                        onClick={() => submitTemplateToMeta(t.key)}
                        disabled={submittingTpl === t.key}
                      >
                        <Send size={14} /> {submittingTpl === t.key ? 'Enviando…' : 'Enviar a Meta'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            <div className="rv-footer-actions">
              <button className="rv-btn rv-btn-primary" onClick={saveSettings} disabled={saving}>
                <Save size={16} /> {saving ? 'Guardando…' : 'Guardar cambios de templates'}
              </button>
            </div>
          </section>
        </div>
      ) : (
        <div className="rv-list">
          <div className="rv-stats-row">
            <div className="rv-stat">
              <div className="rv-stat-label">Total enviados</div>
              <div className="rv-stat-value">{stats.total}</div>
            </div>
            <div className="rv-stat">
              <div className="rv-stat-label">Respondidos</div>
              <div className="rv-stat-value">{stats.rated}</div>
            </div>
            <div className="rv-stat">
              <div className="rv-stat-label">Promedio</div>
              <div className="rv-stat-value">{stats.avg.toFixed(2)} <Star size={14} color="#f59e0b" fill="#f59e0b" /></div>
            </div>
            <div className="rv-stat">
              <div className="rv-stat-label">Con foto</div>
              <div className="rv-stat-value"><Camera size={14} /> {stats.withPhoto}</div>
            </div>
            <div className="rv-stat">
              <div className="rv-stat-label">Cupón generado</div>
              <div className="rv-stat-value"><Ticket size={14} /> {stats.withCoupon}</div>
            </div>
            <button
              className="rv-btn rv-btn-secondary"
              onClick={exportReviewsCsv}
              title="Exportar reviews con rating a CSV"
              disabled={reviewsLoading}
            >
              <Download size={14} /> CSV
            </button>
            <button
              className="rv-btn rv-btn-secondary"
              onClick={exportRevieCsv}
              title="Exportar para importar en Revie: solo reviews con estrellas o comentario Y producto TN asociado. Una línea por producto al que aplica (ej. Parlante Negro y Blanco = 2 líneas)."
              disabled={reviewsLoading}
            >
              <Download size={14} /> CSV Revie
            </button>
            <button className="rv-btn rv-btn-secondary" onClick={() => loadReviews()} title="Refrescar">
              <RefreshCw size={14} />
            </button>
          </div>

          <div className="rv-rating-dist">
            {stats.ratingDist.slice().reverse().map(d => {
              const pct = stats.rated > 0 ? Math.round((d.count / stats.rated) * 100) : 0;
              return (
                <div key={d.rating} className="rv-dist-row">
                  <span className="rv-dist-label">{d.rating}⭐</span>
                  <div className="rv-dist-bar"><div className="rv-dist-fill" style={{ width: `${pct}%` }} /></div>
                  <span className="rv-dist-count">{d.count}</span>
                </div>
              );
            })}
          </div>

          {(() => {
            const fcounts = filterCounts;
            const opts: { k: 'all' | 'reclamo' | 'rated' | 'sent'; label: string }[] = [
              { k: 'all', label: 'Todas' },
              { k: 'reclamo', label: '🔴 Reclamos' },
              { k: 'rated', label: 'Con rating' },
              { k: 'sent', label: 'Sin responder' },
            ];
            return (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '4px 0 14px' }}>
                {opts.map(o => (
                  <button key={o.k} onClick={() => { setListFilter(o.k); loadReviews({ page: 0, filter: o.k }); }}
                    style={{
                      padding: '5px 12px', borderRadius: 999, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                      border: `1px solid ${listFilter === o.k ? '#941E21' : 'var(--border)'}`,
                      background: listFilter === o.k ? 'rgba(148,30,33,0.1)' : 'var(--card)',
                      color: listFilter === o.k ? '#941E21' : 'var(--text-muted, #94a3b8)',
                    }}>
                    {o.label} <span style={{ opacity: 0.7 }}>{fcounts[o.k]}</span>
                  </button>
                ))}
              </div>
            );
          })()}

          {reviewsLoading ? (
            <div className="rv-loading">Cargando…</div>
          ) : reviews.length === 0 ? (
            <div className="rv-empty">Todavía no hay reviews.</div>
          ) : (
            <div className="rv-table-wrap">
              <table className="rv-table">
                <thead>
                  <tr>
                    <th>Pedido</th>
                    <th>Cliente</th>
                    <th>Producto</th>
                    <th>Rating</th>
                    <th>Comentario</th>
                    <th>Foto</th>
                    <th>Cupón</th>
                    <th>Estado</th>
                    <th>Enviado</th>
                    <th title="Estado del pedido de review en WhatsApp (desde el 18/10)">WhatsApp</th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.filter(r =>
                    listFilter === 'all' ? true :
                    listFilter === 'reclamo' ? r.status === 'reclamo' :
                    listFilter === 'rated' ? r.rating != null :
                    r.status === 'sent'
                  ).map(r => (
                    <tr key={r.id}>
                      <td>{r.order_id}</td>
                      <td>{r.contact_name || r.phone}</td>
                      <td style={{ maxWidth: 220 }}>
                        {(() => { const s = productsSummary(r.orders); return s ? (
                          <span title={s} style={{ display: 'inline-block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                            {s}
                          </span>
                        ) : <span className="rv-dim">—</span>; })()}
                      </td>
                      <td>{r.rating ? '⭐'.repeat(r.rating) : <span className="rv-dim">—</span>}</td>
                      <td style={{ maxWidth: 280 }}>
                        {r.comment ? (
                          <span title={r.comment} style={{ display: 'inline-block', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                            {r.comment}
                          </span>
                        ) : <span className="rv-dim">—</span>}
                      </td>
                      <td>{(() => {
                        const media = Array.isArray(r.media) ? r.media : [];
                        const items: { url: string; mime?: string }[] = [];
                        if (r.photo_url && !media.some(m => m.url === r.photo_url)) items.push({ url: r.photo_url, mime: 'image/jpeg' });
                        items.push(...media);
                        if (items.length === 0) return <span className="rv-dim">—</span>;
                        return <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                          {items.map((m, i) => {
                            const mime = m.mime || '';
                            const icon = mime.startsWith('video') ? '🎥' : mime.startsWith('audio') ? '🎧' : mime.startsWith('image') ? <Camera size={14} /> : '📎';
                            return <a key={i} href={m.url} target="_blank" rel="noreferrer" title={mime}>{icon}</a>;
                          })}
                        </div>;
                      })()}</td>
                      <td>{r.discount_code ? <code className="rv-coupon">{r.discount_code}</code> : <span className="rv-dim">—</span>}</td>
                      <td><span className={`rv-row-status status-${r.status}`}>{r.status}</span></td>
                      <td>{new Date(r.sent_at).toLocaleDateString('es-AR')}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{(() => {
                        const d = waDelivery.get(String(r.id));
                        const hm = (iso: string) => new Date(iso).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
                        if (!d) return <span className="rv-dim">—</span>;
                        if (d.status === 'failed') return <span style={{ color: '#dc2626' }} title={d.error || ''}>✗ No llegó</span>;
                        if (d.read_at) return <span style={{ color: '#2563eb', fontWeight: 600 }} title={`Leído ${hm(d.read_at)}`}>✓✓ Leído <span className="rv-dim">{hm(d.read_at)}</span></span>;
                        if (d.delivered_at) return <span title={`Entregado ${hm(d.delivered_at)}`}>✓✓ Entregado</span>;
                        return <span className="rv-dim" title="Enviado. El estado de entrega se registra desde el 18/10">✓ Enviado</span>;
                      })()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {hasMore && (
                <div style={{ textAlign: 'center', padding: '14px 0 4px' }}>
                  <button className="rv-btn rv-btn-secondary" disabled={loadingMore}
                    onClick={() => loadReviews({ page: Math.floor(reviews.length / PAGE_SIZE) })}>
                    {loadingMore ? 'Cargando…' : `Cargar 100 más (mostrando ${reviews.length} de ${filterCounts[listFilter === 'all' ? 'all' : listFilter]})`}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Reviews;
